import { useEffect, useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import DashboardLayout from "../dashboard/_components/DashboardLayout";
import {
  collection, getDocs, query, orderBy, doc, updateDoc,
} from "firebase/firestore";
import {
  ShieldCheck, Search, Filter, RefreshCw, Clock, CheckCircle,
  XCircle, Eye, FileText, CreditCard, User, Image as ImageIcon,
  X, Camera, Receipt, ShoppingBag, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const PERSONAL_DOCS = [
  { key: "aadharFront", label: "Aadhar Front", icon: CreditCard },
  { key: "aadharBack", label: "Aadhar Back", icon: CreditCard },
  { key: "panFront", label: "PAN Front", icon: CreditCard },
  { key: "panBack", label: "PAN Back", icon: CreditCard },
  { key: "form16", label: "Form 16", icon: FileText },
  { key: "salarySlip", label: "Salary Slips", icon: Receipt },
  { key: "selfie", label: "Selfie", icon: Camera },
];

const BUSINESS_DOCS = [
  { key: "aadharFront", label: "Aadhar Front", icon: CreditCard },
  { key: "aadharBack", label: "Aadhar Back", icon: CreditCard },
  { key: "panCardFront", label: "PAN Front", icon: CreditCard },
  { key: "panCardBack", label: "PAN Back", icon: CreditCard },
  { key: "selfie", label: "Selfie", icon: Camera },
  { key: "shopPhoto", label: "Shop Photo", icon: ShoppingBag },
  { key: "lastSixMonthBill", label: "6 Month Bill", icon: Receipt },
  { key: "gstDocument", label: "GST Document", icon: FileText },
  { key: "shopStockPhoto", label: "Shop Stock Photo", icon: ImageIcon },
];

export default function DocumentVerification() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterDocStatus, setFilterDocStatus] = useState("all");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [updatingDoc, setUpdatingDoc] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      if (u) setUser(u);
      else navigate("/login");
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => { if (user) fetchAllLoans(); }, [user]);

  const fetchCollection = async (name) => {
    const results = [];
    try {
      const q = query(collection(db, name), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
    } catch {
      try {
        const snap = await getDocs(collection(db, name));
        snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      } catch { /* silent */ }
    }
    return results;
  };

  const fetchAllLoans = async () => {
    setLoading(true);
    try {
      const [personal, business] = await Promise.all([
        fetchCollection("personalLoan"),
        fetchCollection("businessLoans"),
      ]);
      setLoans([
        ...personal.map((d) => ({ ...d, loanType: "Personal Loan", docConfig: PERSONAL_DOCS, col: "personalLoan" })),
        ...business.map((d) => ({ ...d, loanType: "Business Loan", docConfig: BUSINESS_DOCS, col: "businessLoans" })),
      ]);
    } catch (err) {
      console.error("Failed to fetch loans", err);
    } finally {
      setLoading(false);
    }
  };

  const loansWithStats = useMemo(() => loans.map((loan) => {
    const verification = loan.documentVerification || {};
    const docs = loan.documents || {};
    let total = 0, verified = 0, rejected = 0;
    loan.docConfig.forEach(({ key }) => {
      if (docs[key]) {
        total++;
        if (verification[key] === "verified") verified++;
        else if (verification[key] === "rejected") rejected++;
      }
    });
    return { ...loan, _total: total, _verified: verified, _rejected: rejected, _pending: total - verified - rejected, _allDone: (total - verified - rejected) === 0 && total > 0 };
  }), [loans]);

  const filteredLoans = loansWithStats.filter((loan) => {
    const name = loan.fullName || loan.businessName || "";
    const matchSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.mobileNumber?.includes(searchTerm) ||
      loan.aadharNumber?.includes(searchTerm) ||
      loan.panNumber?.includes(searchTerm);
    const matchType = filterType === "all" ||
      (filterType === "personal" && loan.loanType === "Personal Loan") ||
      (filterType === "business" && loan.loanType === "Business Loan");
    let matchStatus = true;
    if (filterDocStatus === "pending") matchStatus = loan._pending > 0;
    else if (filterDocStatus === "verified") matchStatus = loan._allDone;
    else if (filterDocStatus === "rejected") matchStatus = loan._rejected > 0;
    else if (filterDocStatus === "no-docs") matchStatus = loan._total === 0;
    return matchSearch && matchType && matchStatus;
  });

  const stats = useMemo(() => {
    let totalDocs = 0, verified = 0, rejected = 0, pending = 0;
    loansWithStats.forEach((l) => { totalDocs += l._total; verified += l._verified; rejected += l._rejected; pending += l._pending; });
    return { totalDocs, verified, rejected, pending };
  }, [loansWithStats]);

  const updateDocStatus = async (loan, docKey, status) => {
    const updateKey = `${loan.id}-${docKey}`;
    setUpdatingDoc(updateKey);
    try {
      const current = { ...(loan.documentVerification || {}) };
      if (status === "reset") delete current[docKey];
      else current[docKey] = status;
      await updateDoc(doc(db, loan.col, loan.id), {
        documentVerification: current, updatedAt: new Date(), updatedBy: user.uid,
      });
      setLoans((prev) => prev.map((l) =>
        l.id === loan.id && l.col === loan.col
          ? { ...l, documentVerification: current, updatedAt: new Date() } : l,
      ));
      if (status === "verified") toast.success(`${docKey} verified`);
      else if (status === "rejected") toast.error(`${docKey} rejected`);
      else toast.info(`${docKey} reset to pending`);
    } catch { toast.error("Failed to update"); }
    finally { setUpdatingDoc(null); }
  };

  const formatDate = (ts) => {
    if (!ts) return "N/A";
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "N/A"; }
  };

  const getDocStatus = (loan, key) => loan.documentVerification?.[key] || "pending";

  if (!user) return null;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <div className="p-1.5 bg-indigo-600 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              Document Verification
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              Review and verify uploaded documents across all loan applications
            </p>
          </div>
          <button onClick={fetchAllLoans} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline text-xs font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
        {[
          { label: "Total Documents", value: stats.totalDocs, bg: "bg-blue-100", icon: FileText, color: "text-blue-600" },
          { label: "Pending", value: stats.pending, bg: "bg-yellow-100", icon: Clock, color: "text-yellow-600" },
          { label: "Verified", value: stats.verified, bg: "bg-green-100", icon: CheckCircle, color: "text-green-600" },
          { label: "Rejected", value: stats.rejected, bg: "bg-red-100", icon: XCircle, color: "text-red-600" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-gray-600 text-xs font-medium">{s.label}</p>
                <div className={`p-2 ${s.bg} rounded-lg`}><Icon className={`w-4 h-4 ${s.color}`} /></div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{s.value}</h3>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-5">
        <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
          <div className="relative flex-1">
            <input type="text" placeholder="Search by name, mobile, Aadhar, PAN..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 sm:py-2.5 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm" />
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 shrink-0" />
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 sm:py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
              <option value="all">All Loans</option>
              <option value="personal">Personal Loans</option>
              <option value="business">Business Loans</option>
            </select>
            <select value={filterDocStatus} onChange={(e) => setFilterDocStatus(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 sm:py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Fully Verified</option>
              <option value="rejected">Has Rejected</option>
              <option value="no-docs">No Documents</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 text-center">Loading documents...</p>
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg font-medium">No documents found</p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm ? "Try adjusting your search terms" : "Documents will appear here when uploaded"}
                </p>
              </div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Applicant</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Loan Type</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">Applied</th>
                  <th className="px-4 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                  <th className="px-4 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredLoans.map((loan) => {
                  const rowId = `${loan.loanType}-${loan.id}`;
                  const isExpanded = expandedId === rowId;
                  const pct = loan._total > 0 ? Math.round(((loan._verified + loan._rejected) / loan._total) * 100) : 0;
                  return (
                    <Fragment key={rowId}>
                      <tr className="transition-colors hover:bg-gray-50/50">
                        <td className="px-4 sm:px-6 py-3">
                          <button onClick={() => setExpandedId(isExpanded ? null : rowId)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                          </button>
                        </td>
                        <td className="px-4 sm:px-6 py-3">
                          <div>
                            <span className="font-semibold text-sm text-gray-900">{loan.fullName || loan.businessName || "N/A"}</span>
                            <div className="text-xs text-gray-500 mt-0.5">{loan.mobileNumber || "N/A"}</div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${loan.loanType === "Personal Loan" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                            {loan.loanType}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 hidden md:table-cell text-sm text-gray-500">{formatDate(loan.createdAt)}</td>
                        <td className="px-4 sm:px-6 py-3">
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-green-600 font-semibold">{loan._verified}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-600">{loan._total}</span>
                            </div>
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => setExpandedId(isExpanded ? null : rowId)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View Documents">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => navigate(loan.loanType === "Personal Loan" ? `/personal-loans/${loan.id}` : `/business-loans/${loan.id}`)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="View Full Details">
                              <FileText className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Row - Document Grid */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-4 sm:px-6 py-4 bg-gray-50/80">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                              {loan.docConfig.map(({ key, label, icon: DocIcon }) => {
                                const url = loan.documents?.[key];
                                const status = getDocStatus(loan, key);
                                const isUpdating = updatingDoc === `${loan.id}-${key}`;
                                const isPdf = url?.toLowerCase().includes(".pdf");
                                return (
                                  <div key={key} className={`bg-white rounded-xl border-2 p-3 transition-all ${status === "verified" ? "border-green-200" : status === "rejected" ? "border-red-200" : "border-gray-200"}`}>
                                    {/* Doc header */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <DocIcon className="w-4 h-4 text-gray-500" />
                                        <span className="text-xs font-semibold text-gray-700">{label}</span>
                                      </div>
                                      {url ? (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${status === "verified" ? "bg-green-100 text-green-800" : status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                                          {status === "verified" ? <CheckCircle className="w-3 h-3" /> : status === "rejected" ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                          {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-gray-400 font-medium">Not uploaded</span>
                                      )}
                                    </div>
                                    {/* Preview */}
                                    {url ? (
                                      <button onClick={() => setPreviewDoc({ url, label, isPdf })}
                                        className="w-full mb-2">
                                        {isPdf ? (
                                          <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center gap-2 hover:border-indigo-400 transition-colors">
                                            <FileText className="w-10 h-10 text-indigo-500" />
                                            <span className="text-xs font-medium text-indigo-700">View PDF</span>
                                          </div>
                                        ) : (
                                          <img src={url} alt={label} className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-indigo-400 transition-colors" />
                                        )}
                                      </button>
                                    ) : (
                                      <div className="h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                                        <p className="text-gray-400 text-xs">Not available</p>
                                      </div>
                                    )}
                                    {/* Action buttons */}
                                    {url && (
                                      <div className="flex gap-1.5">
                                        <button disabled={isUpdating || status === "verified"}
                                          onClick={() => updateDocStatus(loan, key, "verified")}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                                          <CheckCircle className="w-3 h-3" /> Verify
                                        </button>
                                        <button disabled={isUpdating || status === "rejected"}
                                          onClick={() => updateDocStatus(loan, key, "rejected")}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                                          <XCircle className="w-3 h-3" /> Reject
                                        </button>
                                        {status !== "pending" && (
                                          <button disabled={isUpdating}
                                            onClick={() => updateDocStatus(loan, key, "reset")}
                                            className="px-2 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-[11px] font-semibold disabled:opacity-40">
                                            Reset
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {/* Results count */}
        {!loading && filteredLoans.length > 0 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-sm text-gray-600">
              Showing {filteredLoans.length} application{filteredLoans.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">{previewDoc.label}</h3>
              <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-70px)]">
              {previewDoc.isPdf ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FileText className="w-20 h-20 text-indigo-500 mb-4" />
                  <a href={previewDoc.url} target="_blank" rel="noopener noreferrer"
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium">
                    Open PDF in New Tab
                  </a>
                </div>
              ) : (
                <img src={previewDoc.url} alt={previewDoc.label} className="w-full rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
